import React, { useState } from 'react';
import { Video, Shield, Lock, X, Check } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Video Call Consent Modal - DPDPA 2023 Compliant
 * Displays before first video call to obtain explicit consent
 */
const DPDPAVideoCallConsent = ({ isOpen, onAccept, onDecline }) => {
  const [hasReadAll, setHasReadAll] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  if (!isOpen) return null;

  const handleAccept = () => {
    if (agreedToTerms && hasReadAll) {
      onAccept();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-fadeIn">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                <Video className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Video Call Consent</h2>
                <p className="text-blue-100 text-sm">DPDPA 2023 Compliance Notice</p>
              </div>
            </div>
            <Shield className="w-8 h-8 text-blue-200" />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-280px)]">
          <div className="space-y-6">
            {/* Introduction */}
            <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
              <p className="text-sm text-gray-700">
                Before you proceed with video calling, we need your explicit consent as required by the 
                <strong> Digital Personal Data Protection Act (DPDPA) 2023</strong>.
              </p>
            </div>

            {/* How Video Calls Work */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Lock className="w-5 h-5 text-green-600" />
                How Our Video Calls Work
              </h3>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>Peer-to-Peer Technology:</strong> Your video and audio are transmitted directly 
                    between you and your psychologist. They do NOT pass through our servers.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>End-to-End Encrypted:</strong> All video and audio streams are encrypted using 
                    WebRTC's built-in encryption (DTLS-SRTP).
                  </p>
                </div>
                <div className="flex gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>Video Not Recorded:</strong> INSIGHT platform does NOT record your video streams. 
                    Video sessions are completely private and not stored.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>Call Metadata Logging:</strong> Only call metadata (date, duration, participants) 
                    is logged for audit, billing, and quality assurance purposes. Video/audio content is NEVER stored.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p>
                    <strong>Minimal Data Collection:</strong> Only temporary connection data (signaling) 
                    passes through our server to establish the call, which is automatically deleted.
                  </p>
                </div>
              </div>
            </div>

            {/* What Data We Collect */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                What Data We Process
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Video/Audio Streams:</span>
                  <span className="font-semibold text-green-600">NOT stored or recorded</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Call Metadata (time, duration):</span>
                  <span className="font-semibold text-blue-600">Logged for 90 days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Participant IDs:</span>
                  <span className="font-semibold text-blue-600">Logged for 90 days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Signaling Data (connection):</span>
                  <span className="font-semibold text-gray-900">Temporary (auto-deleted)</span>
                </div>
              </div>
              
              {/* Call Logging Details */}
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2 text-sm flex items-center gap-2">
                  <Lock className="w-4 h-4 text-amber-600" />
                  About Call Metadata Logging
                </h4>
                <ul className="text-xs text-gray-700 space-y-1.5">
                  <li>• <strong>Purpose:</strong> Audit trail, dispute resolution, quality assurance, and billing verification</li>
                  <li>• <strong>What's Logged:</strong> Call start/end times, duration, consultation ID, participant IDs (not video/audio)</li>
                  <li>• <strong>Retention:</strong> Automatically deleted after 90 days (DPDPA compliance)</li>
                  <li>• <strong>Access:</strong> Only you and your assigned psychologist can view your call history</li>
                  <li>• <strong>Your Control:</strong> You can manually delete your call history anytime before auto-deletion</li>
                  <li>• <strong>Security:</strong> Participant names encrypted in database, decrypted only when displayed</li>
                </ul>
              </div>
            </div>

            {/* Your Rights */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Your Rights Under DPDPA 2023
              </h3>
              <div className="text-sm text-gray-700 space-y-2">
                <p>✓ Access your video call history metadata</p>
                <p>✓ Delete your video call logs at any time (Settings → Video History)</p>
                <p>✓ Download all your data (Account Settings)</p>
                <p>✓ Withdraw consent at any time (in Account Settings)</p>
                <p>✓ Use alternative consultation methods (phone/in-person)</p>
              </div>
            </div>

            {/* Data Retention */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Data Retention Period
              </h3>
              <div className="text-sm text-gray-700 space-y-1">
                <p>• <strong>Signaling Data:</strong> Automatically deleted after call ends</p>
                <p>• <strong>Call Metadata:</strong> Retained for 3 years as per medical records requirement</p>
                <p>• <strong>Audio Transcripts:</strong> Retained for 3 years with consultation records (can be deleted on request)</p>
                <p>• <strong>Video/Audio Streams:</strong> Never stored (peer-to-peer only)</p>
              </div>
            </div>

            {/* Consent Checkboxes */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="readAll"
                  checked={hasReadAll}
                  onChange={(e) => setHasReadAll(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="readAll" className="text-sm text-gray-700 cursor-pointer">
                  I have read and understood how video calls work on INSIGHT platform
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="agreeTerms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  disabled={!hasReadAll}
                />
                <label 
                  htmlFor="agreeTerms" 
                  className={`text-sm ${hasReadAll ? 'text-gray-700 cursor-pointer' : 'text-gray-400'}`}
                >
                  <strong>I consent to:</strong> (1) Video/audio transmission for consultation purposes, 
                  (2) Logging of call metadata (date, time, duration, participants) for audit and billing purposes, 
                  and (3) Automatic deletion of call logs after 90 days. I understand that video/audio content is 
                  NEVER recorded or stored.
                </label>
              </div>
            </div>

            {/* Additional Info */}
            <div className="text-xs text-gray-500 border-t pt-4">
              <p>
                By providing consent, you agree to the processing of your personal data for video calling purposes 
                as outlined in our <a href="/privacy-policy" target="_blank" className="text-blue-600 underline">Privacy Policy</a>.
                Your consent will be logged with a timestamp for DPDPA 2023 compliance.
              </p>
              <p className="mt-2">
                <strong>Data Retention:</strong> Call metadata is automatically deleted after 90 days. You can manually delete your call history anytime.
              </p>
              <p className="mt-2">
                For questions or to exercise your rights, contact us at: <strong>privacy@insight-platform.com</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
          <Button
            variant="outline"
            onClick={onDecline}
            className="flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Decline
          </Button>

          <Button
            onClick={handleAccept}
            disabled={!agreedToTerms || !hasReadAll}
            className={`flex items-center gap-2 ${
              agreedToTerms && hasReadAll
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            <Check className="w-4 h-4" />
            Accept & Continue
          </Button>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DPDPAVideoCallConsent;
